// Reverse/forward mapping helpers for SecondaryTherapyDialogNew.
// Ported from src/components/templates/SecondaryTherapyDialog/SecondaryTherapyDialog.tsx (antd Form +
// moment), replacing moment with dayjs and the antd Form state with a flat React state object.
import dayjs from 'dayjs'
import {REMINDER_STATUSES, SecondaryTherapyDataSchemaElement} from 'src/constants/secondaryTherapyFields'
import {UnitValue} from './UnitValueField'
import {
  DEFAULT_CALCULATION_CRITERION_UNIT_TYPE,
  DEFAULT_DOSAGE_UNIT_TYPE,
  DEFAULT_FREQUENCY_UNIT_TYPE,
  DEFAULT_INFUSION_RATE_UNIT_TYPE,
  DEFAULT_SPEED_UNIT_TYPE,
  DEFAULT_TOLERANCE_DAYS_UNIT_TYPE,
} from '../../patient/tabs/therapy/therapyOptions'

export interface FrequencyValue {
  asNeeded?: boolean
  times?: number
  each: UnitValue
  days?: string[]
  notificationTime?: string | null
  toleranceDays: UnitValue
}

export interface CustomDosage {
  dosage: UnitValue
  infusionRate: UnitValue
  secondaryDosage: UnitValue
  dilution?: number
  administrationHour?: string | null
}

export interface DosageValue {
  assumptionDosage: UnitValue
  infusionRate: UnitValue
  secondaryDosage: UnitValue
  dilution?: number
  drugDosage: UnitValue
  dailyDosage: UnitValue
  weeklyDosage: UnitValue
  monthlyDosage: UnitValue
  vialDosage: UnitValue
  calculationCriterion: UnitValue
  customDosages?: CustomDosage[]
}

export interface ModeOfAssumptionValue {
  modeOfAssumption?: string
  deviceOfAssumption?: string
  speedOfDevice: UnitValue
}

// Composite value passed to FrequencyStep: the drug's own intake frequency
// plus the therapy's validity period (startDate/endDate), which are independent top-level fields.
export interface FrequencyStepValue {
  frequency: FrequencyValue
  startDate: string | null
  endDate: string | null
}

export interface SecondaryTherapyFormState {
  drugId?: string
  frequency: FrequencyValue
  startDate: string | null
  endDate: string | null
  assumptionDosage: UnitValue
  infusionRate: UnitValue
  secondaryDosage: UnitValue
  dilution?: number
  drugDosage: UnitValue
  dailyDosage: UnitValue
  weeklyDosage: UnitValue
  monthlyDosage: UnitValue
  vialDosage: UnitValue
  calculationCriterion: UnitValue
  customDosages?: CustomDosage[]
  modeOfAssumption?: string
  deviceOfAssumption?: string
  speedOfDevice: UnitValue
  notes: string
  // Only ever set (and only ever submitted) on CREATE — see buildSubmitBody. On edit it is read
  // back from the therapy for completeness but never sent, since the list-level ReminderToggle in
  // TherapiesNew.tsx is the only writer of this field once the therapy already exists.
  reminderStatus?: string
}

// A time can arrive from the backend as a plain 'HH:mm' string (legacy submit format). The *New time
// pickers are controlled with ISO strings, so incoming 'HH:mm' values must be converted to ISO first.
const timeToIso = (time?: string | null): string | null => {
  if (!time) return null
  const parsed = dayjs(time, 'HH:mm')
  return parsed.isValid() ? parsed.toISOString() : null
}

const isoToTime = (value?: string | null): string | undefined =>
  value ? dayjs(value).format('HH:mm') : undefined

const buildUnitValue = (defaultUnitType: string, dosage?: Record<string, any>): UnitValue => ({
  unitType: defaultUnitType,
  ...dosage,
})

const buildCustomDosages = (customDosages?: Record<string, any>[]): CustomDosage[] | undefined =>
  customDosages?.map((customDosage) => ({
    dosage: {value: customDosage.value, unitType: customDosage.unitType},
    infusionRate: customDosage.infusionRate ?? {},
    secondaryDosage: customDosage.secondaryDosage ?? {},
    dilution: customDosage.dilution,
    administrationHour: timeToIso(customDosage.administrationHour),
  }))

export function buildInitialFormState(secondaryTherapy?: Record<string, any>): SecondaryTherapyFormState {
  // frequency.date ("Inizio assunzione farmaco") is no longer collected: drop it from legacy records
  const {date: _date, ...frequencyWithoutDate} = secondaryTherapy?.frequency ?? {}
  return {
    drugId: secondaryTherapy?.drug?.drugId,
    startDate: secondaryTherapy?.startDate ?? null,
    endDate: secondaryTherapy?.endDate ?? null,
    frequency: {
      ...frequencyWithoutDate,
      each: buildUnitValue(DEFAULT_FREQUENCY_UNIT_TYPE, secondaryTherapy?.frequency?.each),
      toleranceDays: buildUnitValue(DEFAULT_TOLERANCE_DAYS_UNIT_TYPE, secondaryTherapy?.frequency?.toleranceDays),
      notificationTime: timeToIso(secondaryTherapy?.frequency?.notificationTime),
    },
    assumptionDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.assumptionDosage),
    infusionRate: buildUnitValue(DEFAULT_INFUSION_RATE_UNIT_TYPE, secondaryTherapy?.assumptionDosage?.infusionRate),
    secondaryDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.assumptionDosage?.secondaryDosage),
    dilution: secondaryTherapy?.assumptionDosage?.dilution,
    drugDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.drugDosage),
    dailyDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.dailyDosage),
    weeklyDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.weeklyDosage),
    monthlyDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.monthlyDosage),
    vialDosage: buildUnitValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.vialDosage),
    calculationCriterion: buildUnitValue(DEFAULT_CALCULATION_CRITERION_UNIT_TYPE, secondaryTherapy?.calculationCriterion),
    customDosages: buildCustomDosages(secondaryTherapy?.customDosages),
    modeOfAssumption: secondaryTherapy?.modeOfAssumption,
    deviceOfAssumption: secondaryTherapy?.deviceOfAssumption,
    speedOfDevice: buildUnitValue(DEFAULT_SPEED_UNIT_TYPE, secondaryTherapy?.speedOfDevice),
    notes: secondaryTherapy?.notes ?? '',
    reminderStatus: secondaryTherapy?.reminderStatus ?? REMINDER_STATUSES.ACTIVE,
  }
}

const buildPair = (value?: number, unitType?: string): UnitValue | undefined =>
  (value !== undefined && value !== null && unitType) ? {value, unitType} : undefined

const cleanDosage = (dosage?: UnitValue): UnitValue | undefined =>
  (dosage?.value !== undefined && dosage?.value !== null) ? dosage : undefined

export function buildSubmitBody(formState: SecondaryTherapyFormState, isEdit: boolean): Record<string, any> {
  const {
    drugId, infusionRate, secondaryDosage, dilution, assumptionDosage,
    drugDosage, dailyDosage, weeklyDosage, monthlyDosage, vialDosage,
    calculationCriterion, speedOfDevice, customDosages, frequency,
    modeOfAssumption, deviceOfAssumption, notes, startDate, endDate, reminderStatus,
  } = formState

  const cleanAssumptionDosage = cleanDosage(assumptionDosage)

  return {
    modeOfAssumption,
    deviceOfAssumption,
    notes,
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
    // reminderStatus is only ever written here on CREATE — an edit's reminder toggle is handled
    // separately by ReminderToggle in TherapiesNew.tsx, at the list level.
    ...(isEdit ? {} : {reminderStatus}),
    drug: {drugId},
    assumptionDosage: cleanAssumptionDosage ? {
      ...cleanAssumptionDosage,
      infusionRate: buildPair(infusionRate?.value, infusionRate?.unitType),
      secondaryDosage: buildPair(secondaryDosage?.value, secondaryDosage?.unitType),
      dilution,
    } : undefined,
    drugDosage: cleanDosage(drugDosage),
    dailyDosage: cleanDosage(dailyDosage),
    weeklyDosage: cleanDosage(weeklyDosage),
    monthlyDosage: cleanDosage(monthlyDosage),
    vialDosage: cleanDosage(vialDosage),
    calculationCriterion: buildPair(calculationCriterion?.value, calculationCriterion?.unitType),
    speedOfDevice: buildPair(speedOfDevice?.value, speedOfDevice?.unitType),
    customDosages: customDosages?.length ? customDosages.map((customDosage) => ({
      value: customDosage.dosage?.value,
      unitType: customDosage.dosage?.unitType,
      infusionRate: buildPair(customDosage.infusionRate?.value, customDosage.infusionRate?.unitType),
      secondaryDosage: buildPair(customDosage.secondaryDosage?.value, customDosage.secondaryDosage?.unitType),
      dilution: customDosage.dilution,
      administrationHour: isoToTime(customDosage.administrationHour),
    })) : undefined,
    frequency: frequency ? {
      ...frequency,
      each: buildPair(frequency.each?.value, frequency.each?.unitType),
      toleranceDays: buildPair(frequency.toleranceDays?.value, frequency.toleranceDays?.unitType),
      notificationTime: isoToTime(frequency.notificationTime),
    } : undefined,
  }
}

export function isFrequencyStepValid(frequency: FrequencyValue, dataSchema: SecondaryTherapyDataSchemaElement[]): boolean {
  // "Numero assunzioni"/"Ogni" sono richiesti solo quando configurati: se il configuratore li ha
  // disattivati, non vengono nemmeno mostrati, quindi non possono essere obbligatori.
  if (!dataSchema.some((element) => element.value === 'frequency.times')) return true
  if (frequency.asNeeded) return true
  return Boolean(frequency.times) && frequency.each?.value !== undefined && frequency.each?.value !== null && Boolean(frequency.each?.unitType)
}
